import { Injectable } from '@angular/core';
import { Application } from './_models/feature';

type Theme =
  | 'medi3-theme.css'
  | 'el-theme.css'
  | 'moelven-theme.css'
  | 'agil-theme.css'
  | 'mdco-theme.css'
  | '';

/**
 * Ids of rows in the `Theme` table. A business or user can be pointed at one
 * explicitly, and that choice is what `userTheme.Id` carries.
 *
 * Agil deliberately has no entry here. Its theme is reached through
 * {@link APPLICATION_THEMES} instead, keyed by application, so it needs no
 * `Theme` row and no data migration — see `resolveThemeUrl`.
 */
export enum ThemeId {
  DEFAULT = 1,
  EL = 7,
  MOELVEN = 8,
  MEDI3 = 9,
}

/**
 * Brand stylesheet per application, for tenants whose look is a property of the
 * application rather than something a business picks.
 *
 * An application listed here themes every one of its users automatically. An
 * explicit `Theme` row still wins — see `resolveThemeUrl` — so a business that
 * has deliberately chosen a theme keeps it.
 */
const APPLICATION_THEMES: Partial<Record<Application, Theme>> = {
  [Application.AGIL]: 'agil-theme.css',
  [Application.DIN_HMS]: 'mdco-theme.css',
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private styleLink: HTMLLinkElement | null = null;

  /**
   * @param themeId       `userTheme.Id` from the session, when the user or
   *                      their business points at a `Theme` row.
   * @param applicationId `userBase.ApplicationId`. Supplies the brand theme for
   *                      applications that have one, which is the only way Agil
   *                      gets themed.
   */
  setTheme(themeId?: ThemeId | number | null, applicationId?: number | null): void {
    const themeUrl = this.resolveThemeUrl(themeId, applicationId);

    if (!this.styleLink) {
      this.styleLink = document.createElement('link');
      this.styleLink.id = 'appStyle';
      this.styleLink.rel = 'stylesheet';
      this.styleLink.type = 'text/css';
    }

    if (!themeUrl) {
      this.styleLink.href = '';
    } else {
      const timeNow = (Date.now() / 1000) | 1;
      this.styleLink.href = themeUrl + '?ver=' + timeNow;
    }
  }

  applyTheme(): void {
    if (this.styleLink) {
      if (document.head.contains(this.styleLink)) {
        document.head.removeChild(this.styleLink);
      }
      document.head.appendChild(this.styleLink);
    }
  }

  /**
   * Set and apply in one call. Every caller did both in sequence behind its own
   * copy of a `userTheme?.Stylepath` guard, and that guard is why Agil could
   * not be themed: an application with no `Theme` row never reached `setTheme`
   * at all. Resolution belongs here, so the call sites only have to say who is
   * logged in.
   */
  applyThemeForUser(themeId?: ThemeId | number | null, applicationId?: number | null): void {
    this.setTheme(themeId, applicationId);
    this.applyTheme();
  }

  /**
   * An explicitly chosen theme wins over the application's brand theme:
   * configuration is a decision someone made, and a brand default is only a
   * default. `ThemeId.DEFAULT` and unknown ids resolve to no stylesheet, which
   * is what lets the application theme take over for them.
   */
  private resolveThemeUrl(
    themeId?: ThemeId | number | null,
    applicationId?: number | null
  ): Theme {
    return this.getThemeUrlById(themeId) || this.getApplicationThemeUrl(applicationId);
  }

  private getApplicationThemeUrl(applicationId?: number | null): Theme {
    if (applicationId == null) {
      return '';
    }
    return APPLICATION_THEMES[applicationId as Application] ?? '';
  }

  private getThemeUrlById(themeId?: ThemeId | number | null): Theme {
    switch (themeId) {
      case ThemeId.DEFAULT:
        return '';
      case ThemeId.MEDI3:
        return 'medi3-theme.css';
      case ThemeId.EL:
        return 'el-theme.css';
      case ThemeId.MOELVEN:
        return 'moelven-theme.css';

      default:
        return '';
    }
  }
}
